/**
 * InlineEmailThreadPanel — hosts the email-thread surface as a REAL CHILD of
 * a page's layout (a `SplitView` panel), driven by the same URL-synced
 * slideout stack the global portal renderer uses.
 *
 * Mount this inside a SplitView panel on a page that has claimed the
 * `email_thread` kind via `registerInlineHost(['email_thread'])` — the
 * global `SlideoutRenderer` (and its dim backdrop) stands down for claimed
 * kinds, so exactly one surface renders the thread and nothing overlays the
 * page. Mirrors `InlineCrmObjectPanel`.
 *
 * There is no `SlideoutMenu` here, so this component IS the chrome: it
 * publishes `SurfaceDismissContext` and renders `SlideoutMenu.Header` (which
 * carries the close button) around the same header/body regions the
 * portaled `EmailThreadPanel` uses.
 */

import { type ReactElement, useMemo } from 'react';

import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import {
  SurfaceDismissContext,
  type SurfaceDismissContextValue,
} from '@/lib/base/overlays/surface-dismiss-context';
import { type SlideoutEntry } from '@/lib/crm/contexts/SlideoutStackContext';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';

import {
  EmailThreadBodyContent,
  EmailThreadHeaderContent,
} from './EmailThreadSurface';

export interface InlineEmailThreadPanelProps {
  /** Stack entry this panel renders. Must be `kind: 'email_thread'`. */
  entry: SlideoutEntry;
}

export function InlineEmailThreadPanel({
  entry,
}: InlineEmailThreadPanelProps): ReactElement | null {
  const { stack, popSlideout, clearToProposalBase } = useSlideoutStack();

  // Close mirrors the overlay panel: clear to the proposal base (everything
  // on the actions page, where no proposal entry is on the stack). Back pops
  // one level — offered only when the thread is layered over another entry
  // (e.g. opened from an entity badge inside a record), so the header's
  // SurfaceBackButton renders nothing on a bare thread. The thread surface
  // is read-only, so no unsaved-changes guard is registered here.
  const showBackButton = stack.length > 1;
  const dismiss = useMemo<SurfaceDismissContextValue>(
    () => ({
      close: clearToProposalBase,
      back: showBackButton ? popSlideout : undefined,
      isEntering: false,
      isExiting: false,
    }),
    [clearToProposalBase, popSlideout, showBackButton]
  );

  if (entry.kind !== 'email_thread') {
    return null;
  }

  return (
    <SurfaceDismissContext value={dismiss}>
      <div
        className='flex h-full w-full flex-col relative'
        data-component='inline-email-thread-panel'
      >
        <SlideoutMenu.Header>
          <EmailThreadHeaderContent entry={entry} />
        </SlideoutMenu.Header>
        <SlideoutMenu.Content>
          <EmailThreadBodyContent entry={entry} />
        </SlideoutMenu.Content>
      </div>
    </SurfaceDismissContext>
  );
}
